import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';

import {Button, Icon, Spinner} from '@sail/ui';
import {cancelCircle, customers} from '@sail/icons/react/Icon';

import type {ClientResponse} from '@stripe-internal/conversations-client';

import {useApp} from '../../../core/hooks/appHooks';

const POLL_INTERVAL_MS = 3000;
const buttonCss = {flex: 1, gap: 'small', height: '28px'};

const messages = defineMessages({
  shareScreenButton: {
    id: 'conversations_assistant.screenshareButton.shareScreenButton',
    description: 'Button to share screen',
    defaultMessage: 'Share screen',
  },
  waitingForAgent: {
    id: 'conversations_assistant.screenshareButton.waitingForAgent',
    description: 'Button text when waiting for agent',
    defaultMessage: 'Waiting for agent',
  },
  stopSharing: {
    id: 'conversations_assistant.screenshareButton.stopSharing',
    description: 'Button text when stopping screenshare',
    defaultMessage: 'Stop sharing',
  },
});

type ScreenshareStatus = 'ACTIVE' | 'INACTIVE' | 'WAITING_FOR_AGENT';
type ScreenshareChannel = 'chat' | 'rac';
export type ScreenshareActionName =
  | 'get-screenshare-status'
  | 'set-screenshare-name'
  | 'request-screenshare'
  | 'stop-screenshare';

export type ScreenshareRequestAction = (
  name: ScreenshareActionName,
  data?: Record<string, unknown>,
  waitForResponse?: boolean,
) => Promise<ClientResponse>;

type Props = {
  channel: ScreenshareChannel;
  agentNames?: string[] | null;
  requestAction: ScreenshareRequestAction;
};

function getScreenshareStatus(data: unknown): ScreenshareStatus {
  if (typeof data !== 'object' || data === null || !('state' in data)) {
    return 'INACTIVE';
  }

  const {state} = data as {state?: unknown};
  return state === 'ACTIVE' ||
    state === 'INACTIVE' ||
    state === 'WAITING_FOR_AGENT'
    ? state
    : 'INACTIVE';
}

export function ConversationScreenshareButtonWithRequestAction({
  channel,
  agentNames,
  requestAction,
}: Props) {
  const [screenshareStatus, setScreenshareStatus] =
    useState<ScreenshareStatus>('INACTIVE');

  const firstAgentName = agentNames?.find((name) => name.length > 0);

  const refreshStatus = useCallback(async () => {
    try {
      const response = await requestAction('get-screenshare-status', {}, true);
      setScreenshareStatus(getScreenshareStatus(response.data));
    } catch (error) {
      setScreenshareStatus('INACTIVE');
    }
  }, [requestAction]);

  useEffect(() => {
    refreshStatus();

    const intervalId = window.setInterval(() => {
      refreshStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (!firstAgentName) {
      return;
    }

    requestAction(
      'set-screenshare-name',
      {
        agentName: firstAgentName,
        channel,
      },
      true,
    );
  }, [channel, firstAgentName, requestAction]);

  const requestScreenshare = useCallback(() => {
    requestAction('request-screenshare');
  }, [requestAction]);

  const stopScreenshare = useCallback(() => {
    requestAction('stop-screenshare');
  }, [requestAction]);

  if (screenshareStatus === 'WAITING_FOR_AGENT') {
    return (
      <Button
        size="small"
        onPress={stopScreenshare}
        data-testid="conversations-widget-share-screen-button"
        css={buttonCss}
      >
        <Spinner />
        <FormattedMessage {...messages.waitingForAgent} />
      </Button>
    );
  }

  if (screenshareStatus === 'ACTIVE') {
    return (
      <Button
        size="small"
        onPress={stopScreenshare}
        type="destructive"
        data-testid="conversations-widget-share-screen-button"
        css={buttonCss}
      >
        <Icon icon={cancelCircle} />
        <FormattedMessage {...messages.stopSharing} />
      </Button>
    );
  }

  const isDisabled = !firstAgentName;

  return (
    <Button
      size="small"
      onPress={requestScreenshare}
      data-testid="conversations-widget-share-screen-button"
      css={buttonCss}
      disabled={isDisabled}
    >
      <Icon icon={customers} />
      <FormattedMessage {...messages.shareScreenButton} />
    </Button>
  );
}

export function ConversationScreenshareButton({
  channel,
  agentNames,
}: Omit<Props, 'requestAction'>) {
  const app = useApp();
  const requestAction = useCallback<ScreenshareRequestAction>(
    (name, data = {}, waitForResponse = false) => {
      return app.requestAction({
        action: {
          name,
          data,
        },
        waitForResponse,
      });
    },
    [app],
  );

  return (
    <ConversationScreenshareButtonWithRequestAction
      channel={channel}
      agentNames={agentNames}
      requestAction={requestAction}
    />
  );
}
